import { Hub } from "aws-amplify/utils";
import AWS from "aws-sdk";
import {
  AWSAuthError,
  AWSRequestAbortedError,
  errorReporter,
  type ServiceResult,
  type SeverityLevel,
} from "~/error-handling";
import {
  type CorrelationContext,
  createCorrelationTags,
} from "~/error-handling/correlationContext";
import { toError } from "~/error-handling/errorUtils";
import { showGlobalErrorNotification } from "~/error-handling/serviceErrorUtil";
import { isAuthCredentialError } from "./authErrors";

const isRetryableError = (error: unknown): error is Error => {
  // AWSAuthError always warrants a refresh; otherwise defer to the shared
  // auth-credential classifier (which excludes throttles — see #7299).
  return error instanceof AWSAuthError || isAuthCredentialError(error);
};

// Single-flight: while a force refresh is in progress, concurrent callers share
// the same promise instead of each dispatching another forceTokenRefresh_request.
// configureAuthSession already collapses concurrent force refreshes onto one
// Cognito call, but deduping here too avoids the O(N) Hub event/listener fan-out
// when a whole page's worth of failing queries each ask for a refresh at once.
let pendingTokenRefresh: Promise<void> | null = null;

export const triggerAndAwaitTokenRefresh = (): Promise<void> => {
  if (pendingTokenRefresh !== null) {
    return pendingTokenRefresh;
  }

  pendingTokenRefresh = new Promise<void>((resolve, reject) => {
    // Listener for the refresh result
    const unsubscribe = Hub.listen(
      "application",
      (data: { payload: { event: string } }) => {
        const { event } = data.payload;
        if (event === "forceTokenRefresh_success") {
          unsubscribe();
          resolve();
        } else if (event === "forceTokenRefresh_failure") {
          unsubscribe();
          reject(new Error("Failed to refresh token after request."));
        }
      },
    );

    // Dispatch the request
    Hub.dispatch("application", { event: "forceTokenRefresh_request" });
  }).finally(() => {
    // Clear the slot once settled so the next refresh starts fresh.
    pendingTokenRefresh = null;
  });

  return pendingTokenRefresh;
};

/**
 * Creates a Lambda client wrapper that automatically injects correlation context.
 * This allows all lambdaClient.invoke() calls to automatically get correlation context
 * without changing individual function callsites.
 *
 * @param lambdaClient - Original AWS Lambda client
 * @param correlationContext - Pre-generated correlation context to inject
 * @returns Wrapped Lambda client with correlation context injection
 */
export function wrapLambdaClientWithCorrelation(
  lambdaClient: AWS.Lambda,
  correlationContext: CorrelationContext | null,
): AWS.Lambda {
  // Create a proxy that intercepts invoke calls
  return new Proxy(lambdaClient, {
    get(target, prop, receiver) {
      if (prop === "invoke") {
        return function (params: AWS.Lambda.InvocationRequest) {
          // Use the pre-generated correlation context
          // Enhance payload with correlation context if available
          if (correlationContext && params.Payload) {
            try {
              const payloadStr =
                typeof params.Payload === "string"
                  ? params.Payload
                  : JSON.stringify(params.Payload);
              const originalPayload = JSON.parse(payloadStr) as Record<
                string,
                unknown
              >;
              const enhancedPayload = {
                ...originalPayload,
                _correlationContext: correlationContext,
              };

              // Create new params with enhanced payload
              params = {
                ...params,
                Payload: JSON.stringify(enhancedPayload),
              };
            } catch (error) {
              // If payload parsing fails, continue with original params
              console.warn(
                "Failed to parse Lambda payload for correlation injection:",
                error,
              );
            }
          }

          return target.invoke(params);
        };
      }

      // Return original property for non-invoke calls
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value = Reflect.get(target, prop, receiver);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return value;
    },
  }) as AWS.Lambda;
}

/**
 * A higher-order function that wraps an AWS SDK call to provide automatic retries
 * upon specific, recoverable errors (e.g., expired tokens).
 *
 * This utility does NOT handle token refreshing itself. Instead, it acts as a
 * client to the `AWSProvider`.
 *
 * Workflow:
 * 1. It executes the provided `awsCall`.
 * 2. If the call fails with a retryable error (e.g., "unauthorized", "token expired"),
 *    it sends a `forceTokenRefresh_request` event via the Amplify Hub.
 * 3. It then pauses and waits for `AWSProvider` to handle the refresh and broadcast
 *    a `forceTokenRefresh_success` event.
 * 4. Once the success event is received, it retries the original `awsCall`. Because
 *    `AWSProvider` has re-instantiated the AWS clients, the retry uses the new,
 *    valid credentials.
 *
 * @param awsCall A function that returns a Promise containing the AWS service call.
 * @param maxRetries The maximum number of retry attempts. Defaults to 1.
 * @param options Optional configuration for retry behavior.
 * @returns A `ServiceResult` object with either the data from the successful call or an error.
 */
export const withAWSRetry = async <T>(
  awsCall: () => Promise<T>,
  maxRetries = 1,
  options: {
    baseDelayMs?: number;
    maxJitterMs?: number;
    correlationContext?: CorrelationContext | null;
    silent?: boolean;
  } = {},
): Promise<ServiceResult<T>> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const data = await awsCall();
      return { data, error: null };
    } catch (error) {
      // If we are out of retries OR the error is not retryable, we fail.
      if (attempt >= maxRetries || !isRetryableError(error)) {
        const errorObj = toError(error, "AWS call failed");
        // A request the browser cancelled (navigation/unmount/tab close) isn't
        // a failure anyone can act on, so it's reported below error level —
        // same treatment as an exhausted-retry auth error. It also isn't a
        // notifiable error, so showGlobalErrorNotification below is a no-op.
        const level: SeverityLevel =
          isRetryableError(error) || error instanceof AWSRequestAbortedError
            ? "warning"
            : "error";

        // Include correlation context in error reporting if available
        const reportOptions: {
          level: SeverityLevel;
          tags?: Record<string, string>;
        } = { level };

        if (options.correlationContext) {
          reportOptions.tags = createCorrelationTags(
            options.correlationContext,
          );
        }

        errorReporter.reportApplicationError(errorObj, reportOptions);
        if (!options.silent) {
          showGlobalErrorNotification(errorObj);
        }
        return { data: null, error: errorObj };
      }

      // If we are here, it means we have retries left AND the error is retryable.
      // So we wait with exponential backoff and jitter, then attempt to refresh the token.
      const { baseDelayMs = 1000, maxJitterMs = 5000 } = options;
      const baseDelay = baseDelayMs * (attempt + 1);
      const jitter = Math.random() * maxJitterMs;

      await new Promise((resolve) => setTimeout(resolve, baseDelay + jitter));

      try {
        await triggerAndAwaitTokenRefresh();
        // Success! The loop will continue to the next iteration and retry awsCall.
      } catch (refreshError) {
        // Log the refresh error and add a breadcrumb. Allow the loop to continue.
        // The next attempt will re-try the awsCall, which will likely fail again
        // and trigger another refresh attempt, correctly honoring maxRetries.
        console.error(
          `Token refresh failed on attempt ${attempt + 1}:`,
          refreshError,
        );
        errorReporter.addFeatureBreadcrumb(
          "token-refresh",
          "hub-refresh-failed",
          {
            attempt: attempt + 1,
            error: toError(refreshError).message,
          },
        );
      }
    }
  }

  // This part of the code should be unreachable.
  const unreachableError = new Error(
    "Unexpected error: exited withAWSRetry loop.",
  );
  errorReporter.reportApplicationError(unreachableError, { level: "warning" });
  return { data: null, error: unreachableError };
};
